import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { assert_instanceof, assert_number_gt_0, assert_number_gte_0, assert_true, assert_type_number } from '@shared/asserts/utils_asserts';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { FN_SERIALIZATION__data_deserialize } from '@shared/serialization/FN_SERIALIZATION__data_deserialize';
import { MAP__field_data } from '@shared/types/types';
import { UTILS__number } from '@shared/utils/utils_number';
import { DTO__number_range } from '../../../../settings/DTO__number_range';
import { DTO_GRAPH__instance } from '../../../_graph/DTO_GRAPH__instance';
import { FIELD__base } from '../../_base/FIELD__base';
import { DECORATOR__field_not_linked } from '../_base/DECORATOR__field_not_linked';
import { FIELD_PRIMITIVE__base } from '../_base/FIELD_PRIMITIVE__base';

export class FIELD_PRIMITIVE__base_number extends FIELD_PRIMITIVE__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static = ENUM__dto_class_name.ABSTRACT;

  /*
   *
   */
  static sort_by_value(a: FIELD_PRIMITIVE__base_number, b: FIELD_PRIMITIVE__base_number): number {
    let value_a: number = a.get_value();
    let value_b: number = b.get_value();

    return value_a - value_b;
  }

  /*
   * FIELDS
   */

  multiple_of: number;

  slider_step: number;
  slider_step_values: number[];

  /*
   * PROPERTIES
   */

  /*
   * VALUE
   */

  @DECORATOR__field_not_linked()
  get_value() {
    assert_type_number(this.value);

    return this.value;
  }

  @DECORATOR__field_not_linked()
  set_value(value_new: any): void {
    assert_type_number(value_new);

    if (this.multiple_of == 0) {
      this.value = value_new;
    } else {
      this.value = value_new - (value_new % this.multiple_of);
    }
  }

  /*
   * VALUE RANGE
   */
  @DECORATOR__field_not_linked()
  get_value_range() {
    assert_instanceof(this.value_range, DTO__number_range);

    return this.value_range;
  }

  @DECORATOR__field_not_linked()
  set_value_range(value_new: unknown) {
    assert_instanceof(value_new, DTO__number_range);

    this.value_range = value_new;
  }

  /*
   * VALUES ALLOWED
   */

  @DECORATOR__field_not_linked()
  get_values_allowed() {
    assert_instanceof(this.values_allowed, DTO__number_range);

    return this.values_allowed;
  }

  /*
   * PROPERTIES
   */
  override get grid2d_dimension_values(): number[] {
    assert_true(this.meta.grid2d__is_dimension);

    return this.get_value_range().to_array(this.slider_step);
  }

  /*
   * METHODS
   */

  constructor() {
    super();

    this.slider_step = 1;
    this.slider_step_values = [1];

    this.multiple_of = 0;
    this.value_range = DTO__number_range.CREATE_STUB();
    this.values_allowed = DTO__number_range.CREATE_STUB();
  }

  @DECORATOR__field_not_linked()
  sync_with_field_max(other: FIELD_PRIMITIVE__base_number) {
    /*
     * VALUE
     */

    if (this.get_value() > other.get_value()) {
      this.set_value(other.get_value());
    }

    /*
     * VALUE RANGE
     */

    let this_range = this.get_value_range();

    let this_range_clamp = new DTO__number_range(this.get_values_allowed().min, other.get_value_range().min);

    let this_range_clamped = this_range_clamp.clamp_range(this_range);

    if (!this_range.is_equal(this_range_clamped)) {
      this.set_value_range(this_range_clamped);
    }
  }

  clone(overrides: Partial<this> = {}): this {
    let out = super.clone(overrides);

    if ('value' in overrides) {
      let value = overrides.value;
      assert_type_number(value);

      out.set_value(value);
    }

    return out;
  }

  is_field_is_compatible_to_sync_with(other: FIELD__base) {
    if (!super.is_field_is_compatible_to_sync_with(other)) return false;

    // todo: handle list and value allowed for list
    assert_instanceof(other, FIELD_PRIMITIVE__base_number);

    let out = other.get_values_allowed().is_equal(this.get_values_allowed());

    return out;
  }
  /*
   * VALIDATION
   */
  @DECORATOR__field_not_linked()
  override _validate__case_base(graph: DTO_GRAPH__instance) {
    super._validate__case_base(graph);

    let values_allowed = this.get_values_allowed();

    if (values_allowed.is_stub()) this.error_add(ENUM__error_type.GRAPH_FIELD_NUMBER__VALUES_ALLOWED_IS_STUB);
  }

  override _validate__case_input(graph: DTO_GRAPH__instance) {
    super._validate__case_input(graph);

    let values_allowed = this.get_values_allowed();

    if (values_allowed.is_stub()) this.error_add(ENUM__error_type.GRAPH_FIELD_NUMBER__VALUES_ALLOWED_IS_STUB);

    if (!UTILS__number.is_safe(this.multiple_of)) this.error_add(ENUM__error_type.VALIDATION_INVALID_VALUE);
    if (!UTILS__number.is_safe(this.slider_step)) this.error_add(ENUM__error_type.VALIDATION_INVALID_VALUE);
    if (this.slider_step_values.is_empty()) this.error_add(ENUM__error_type.GRAPH_FIELD_NUMBER__SLIDER_STEPS_ARE_EMPTY);

    let value_range = this.get_value_range();

    if (value_range.is_stub()) this.error_add(ENUM__error_type.GRAPH_FIELD_NUMBER__VALUE_RANGE_IS_STUB);
    if (!values_allowed.is_number_is_in_range(this.get_value())) this.error_add(ENUM__error_type.VALIDATION_INVALID_VALUE);
    if (!values_allowed.is_number_range_is_in_range(value_range)) this.error_add(ENUM__error_type.VALIDATION_INVALID_VALUE);
  }

  try_get_value_from_hovered_grid2d_dimension(field: FIELD__base) {
    if (field.is_stub()) return NaN;
    if (field.meta.graph_id != this.meta.graph_id) return NaN;
    if (field.meta.field_id != this.meta.field_id) return NaN;

    assert_instanceof(field, FIELD_PRIMITIVE__base_number);

    let out = field.get_value();

    return out;
  }

  /*
   *
   */

  @DECORATOR__field_not_linked()
  store__can_deserialize_from_data(map: MAP__field_data) {
    if (!super.store__can_deserialize_from_data(map)) return false;

    /*
     *
     */

    let values_allowed = map.get_or_default('values_allowed', this.values_allowed);
    values_allowed = FN_SERIALIZATION__data_deserialize(values_allowed);

    assert_instanceof(values_allowed, DTO__number_range);
    if (!values_allowed.is_number_range_is_in_range(this.get_values_allowed())) return false;

    /*
     *
     */

    return true;
  }

  /*
   * ASSERTS
   */

  assert_standard_fields_with_value(): void {
    assert_type_number(this.value);

    assert_instanceof(this.value_range, DTO__number_range);

    assert_number_gte_0(this.multiple_of);

    assert_number_gt_0(this.slider_step);
  }
}
